import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Package, Eye, EyeOff, ArrowLeft, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { allCountries } from '../lib/utils';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    country: '',
    address: '',
    city: '',
    postal_code: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (!formData.phone) {
      toast.error('Le numéro de téléphone est obligatoire');
      return;
    }

    if (!formData.address || !formData.city || !formData.postal_code) {
      toast.error('L\'adresse complète est obligatoire pour la livraison de vos colis');
      return;
    }

    setLoading(true);
    
    try {
      const { confirmPassword, address, city, postal_code, ...registerData } = formData;
      // Combine address fields
      registerData.full_address = `${address}, ${postal_code} ${city}`;
      await register(registerData);
      toast.success('Compte créé avec succès !');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'inscription';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4 py-8">
      <div className="w-full max-w-lg">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Retour à l'accueil
        </Link>

        <Card className="shadow-lg border-slate-200">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="font-heading text-2xl">Créer un compte</CardTitle>
            <CardDescription>Rejoignez ISMParcel et expédiez vos colis partout dans le monde</CardDescription>
            <div className="mt-4 text-center">
              <span className="text-slate-600">Déjà un compte ? </span>
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-bold underline">
                Se connecter
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {/* Important Notice */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-bold text-blue-800 mb-1">Important !</p>
                  <p className="text-blue-700">
                    Renseignez votre <strong>adresse complète</strong> et votre <strong>numéro de téléphone</strong>. 
                    Ces informations sont nécessaires pour que nous puissions vous réexpédier vos colis.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Prénom *</Label>
                  <Input
                    id="first_name"
                    placeholder="Jean"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    required
                    data-testid="register-firstname-input"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nom *</Label>
                  <Input
                    id="last_name"
                    placeholder="Dupont"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    required
                    data-testid="register-lastname-input"
                    className="h-12"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  data-testid="register-email-input"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone * <span className="text-xs text-slate-500">(pour vous contacter)</span></Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+33 6 12 34 56 78"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                  data-testid="register-phone-input"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label>Pays de destination *</Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(value) => handleChange('country', value)}
                  required
                >
                  <SelectTrigger className="h-12" data-testid="register-country-select">
                    <SelectValue placeholder="Sélectionnez votre pays" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCountries.map(country => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Address Section */}
              <div className="border-t border-slate-200 pt-4 mt-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Adresse de livraison complète *</p>
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="address">Adresse (rue, numéro) *</Label>
                    <Input
                      id="address"
                      placeholder="123 Avenue des Champs"
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">Code postal *</Label>
                      <Input
                        id="postal_code"
                        placeholder="75001"
                        value={formData.postal_code}
                        onChange={(e) => handleChange('postal_code', e.target.value)}
                        required
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Ville *</Label>
                      <Input
                        id="city"
                        placeholder="Paris"
                        value={formData.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                        required
                        className="h-12"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimum 6 caractères"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    required
                    data-testid="register-password-input"
                    className="h-12 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Répétez votre mot de passe"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  required
                  data-testid="register-confirm-password-input"
                  className="h-12"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? 'Création du compte...' : 'Créer mon compte'}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-lime-50 border border-lime-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-lime-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-700">
                  <p className="font-medium mb-2">À la création de votre compte :</p>
                  <ul className="space-y-1 text-slate-600">
                    <li>• Vous recevrez un <strong>numéro client unique</strong> (ex: CB-XXXXXX)</li>
                    <li>• Notre adresse d'entrepôt pour vos livraisons</li>
                    <li>• <strong className="text-blue-700">Inscrivez toujours votre numéro client sur vos colis</strong> pour qu'on puisse vous identifier !</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center text-sm text-slate-600">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                Se connecter
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
